import type { GenerationPricing } from '@mpr/shared';

import { PRICING_RESOLUTIONS, PRICING_UNITS, pricingTag, type PricingForm } from '../lib/pricing.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';

/** The suffixes a price tag ends with, in the reader's language. */
export function usePriceTag(): (pricing: GenerationPricing | null | undefined) => string | null {
  const { t } = useI18n();
  return (pricing) =>
    pricingTag(pricing, {
      SECOND: t('studioConnectors.pricing.tag.SECOND'),
      IMAGE: t('studioConnectors.pricing.tag.IMAGE'),
      CHARS_10K: t('studioConnectors.pricing.tag.CHARS_10K'),
      TOKENS_1M: t('studioConnectors.pricing.tag.TOKENS_1M'),
      REQUEST: t('studioConnectors.pricing.tag.REQUEST'),
    });
}

/** A short price tag for the connectors table, or nothing when the connector has no price. */
export function PriceTag({ pricing }: { pricing: GenerationPricing | null | undefined }) {
  const tag = usePriceTag()(pricing);
  if (!tag) return null;
  return (
    <div className="tiny price-tag" dir="ltr">
      {tag}
    </div>
  );
}

/**
 * The price a connector's provider charges, and where its reply reports what
 * was used. Accounting only: saving it never asks for a new test.
 */
export function ConnectorPricingFields({
  idPrefix,
  value,
  onChange,
  invalid,
}: {
  idPrefix: string;
  value: PricingForm;
  onChange: (next: PricingForm) => void;
  invalid: boolean;
}) {
  const { t } = useI18n();
  const id = (name: string) => `${idPrefix}-pricing-${name}`;
  const set = (patch: Partial<PricingForm>) => onChange({ ...value, ...patch });
  const number = (name: string, label: TranslationKey, key: 'price' | 'inputPrice' | 'outputPrice' | 'soundPrice') => (
    <>
      <label htmlFor={id(name)}>{t(label)}</label>
      <input
        id={id(name)}
        className="ltr"
        dir="ltr"
        inputMode="decimal"
        value={value[key]}
        placeholder="0.05"
        onChange={(event) => set({ [key]: event.target.value })}
      />
    </>
  );

  return (
    <div className="connector-subgroup connector-pricing">
      <h4>{t('studioConnectors.pricing.title')}</h4>
      <p className="muted small">{t('studioConnectors.pricing.hint')}</p>

      <label htmlFor={id('unit')}>{t('studioConnectors.pricing.unit')}</label>
      <select
        id={id('unit')}
        value={value.unit}
        onChange={(event) => set({ unit: event.target.value as PricingForm['unit'] })}
      >
        <option value="">{t('studioConnectors.pricing.unit.none')}</option>
        {PRICING_UNITS.map((unit) => (
          <option key={unit} value={unit}>
            {t(`studioConnectors.pricing.unit.${unit}` as TranslationKey)}
          </option>
        ))}
      </select>

      {value.unit === 'TOKENS_1M' && (
        <>
          {number('input', 'studioConnectors.pricing.inputPrice', 'inputPrice')}
          {number('output', 'studioConnectors.pricing.outputPrice', 'outputPrice')}
        </>
      )}
      {value.unit !== '' && value.unit !== 'TOKENS_1M' && number('price', 'studioConnectors.pricing.price', 'price')}
      {value.unit === 'SECOND' && (
        <>
          {number('sound', 'studioConnectors.pricing.soundPrice', 'soundPrice')}
          <fieldset className="pricing-resolutions">
            <legend className="small">{t('studioConnectors.pricing.byResolution')}</legend>
            {PRICING_RESOLUTIONS.map((resolution) => (
              <label key={resolution} className="pricing-resolution" htmlFor={id(`res-${resolution}`)}>
                <span className="tiny mono">{resolution}</span>
                <input
                  id={id(`res-${resolution}`)}
                  className="ltr"
                  dir="ltr"
                  inputMode="decimal"
                  value={value.byResolution[resolution]}
                  onChange={(event) =>
                    set({ byResolution: { ...value.byResolution, [resolution]: event.target.value } })
                  }
                />
              </label>
            ))}
          </fieldset>
        </>
      )}
      {invalid && <p className="error small">{t('studioConnectors.pricing.invalid')}</p>}

      <h4>{t('studioConnectors.pricing.usageTitle')}</h4>
      <p className="muted small">{t('studioConnectors.pricing.usageHint')}</p>
      {(
        [
          ['usage-in', 'studioConnectors.pricing.usageInput', 'usageInputTokensPath', 'usage.prompt_tokens'],
          ['usage-out', 'studioConnectors.pricing.usageOutput', 'usageOutputTokensPath', 'usage.completion_tokens'],
          ['usage-sec', 'studioConnectors.pricing.usageSeconds', 'usageSecondsPath', 'usage.duration'],
        ] as const
      ).map(([name, label, key, example]) => (
        <div key={name}>
          <label htmlFor={id(name)}>{t(label)}</label>
          <input
            id={id(name)}
            className="ltr mono"
            dir="ltr"
            value={value[key]}
            placeholder={example}
            onChange={(event) => set({ [key]: event.target.value })}
          />
        </div>
      ))}
    </div>
  );
}
